/** Uma mensagem do chat (F4): avatar, nome, hora, texto (links e @menções), anexos, reações e ações. */
import { createResource, createSignal, For, type JSX, Show } from 'solid-js'
import { Dropdown } from '@ui/components/Dropdown'
import type { ChatAnexo, ChatMensagem } from '@shared/types'
import { corDe, EMOJIS, hora, iniciais, tamanho, urlDoAnexo } from './util'
import { abrirDoc, LINK_DOC } from '../docs/estado'

/** Texto com quebras de linha, links clicáveis e @menções destacadas (nada de HTML do usuário). */
export function TextoRico(props: { texto: string; eu?: { login: string; nome: string } }) {
  const partes = () => props.texto.split(/(\[\[doc:[A-Za-z0-9_-]{8,64}\|[^\]\n]{1,200}\]\]|https?:\/\/[^\s<]+|@[\p{L}\p{N}._-]+)/u)
  const ehMinha = (m: string) => {
    const alvo = m.slice(1).toLowerCase()
    return !!props.eu && (alvo === props.eu.login.toLowerCase() || alvo === props.eu.nome.split(' ')[0].toLowerCase())
  }
  return (
    <span class="whitespace-pre-wrap break-words">
      <For each={partes()}>
        {(p) =>
          LINK_DOC.test(p) ? (
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-md border border-edge bg-page/60 px-2 py-0.5 align-baseline text-sm font-medium text-ink hover:bg-hover"
              title="Abrir no Docs"
              onClick={() => abrirDoc(LINK_DOC.exec(p)![1])}
            >
              {LINK_DOC.exec(p)![2]}
            </button>
          ) : /^https?:\/\//.test(p) ? (
            <a href={p} target="_blank" rel="noopener noreferrer" class="text-accent underline underline-offset-2 break-all">{p}</a>
          ) : p.startsWith('@') && p.length > 1 ? (
            <span class="rounded px-0.5 font-medium" classList={{ 'bg-accent/20 text-accent': ehMinha(p), 'text-accent': !ehMinha(p) }}>{p}</span>
          ) : (
            p
          )
        }
      </For>
    </span>
  )
}

export function Avatar(props: { id: number; nome: string; tamanho?: 'p' | 'm' }) {
  return (
    <span
      class="shrink-0 inline-flex items-center justify-center rounded-md font-semibold text-white select-none"
      classList={{ 'size-8 text-xs': props.tamanho !== 'p', 'size-5 text-[9px]': props.tamanho === 'p' }}
      style={{ background: corDe(props.id) }}
      aria-hidden="true"
    >
      {iniciais(props.nome)}
    </span>
  )
}

function Anexo(props: { a: ChatAnexo }) {
  const imagem = /^image\/(png|jpeg|gif|webp)$/.test(props.a.mime)
  const [url] = createResource(() => (imagem ? props.a.chave : false), (c) => urlDoAnexo(c).catch(() => null))
  const [erro, setErro] = createSignal<string | null>(null)
  const abrir = async () => {
    setErro(null)
    try {
      const u = await urlDoAnexo(props.a.chave)
      const link = document.createElement('a')
      link.href = u
      link.download = props.a.nome
      link.target = '_blank'
      link.click()
    } catch (e) {
      setErro((e as Error).message)
    }
  }
  return (
    <Show
      when={imagem && url()}
      fallback={
        <button type="button" class="flex items-center gap-2 rounded-lg border border-edge bg-page/50 px-3 py-2 text-left hover:bg-hover max-w-xs" onClick={() => void abrir()} title="Abrir / baixar">
          <span class="text-lg" aria-hidden="true">📎</span>
          <span class="min-w-0">
            <span class="block truncate text-sm text-ink">{props.a.nome}</span>
            <span class="block text-xs text-ink-subtle">{erro() ?? tamanho(props.a.tamanho)}</span>
          </span>
        </button>
      }
    >
      <button type="button" class="block rounded-lg overflow-hidden border border-edge hover:opacity-90" onClick={() => void abrir()} title={props.a.nome}>
        <img src={url()!} alt={props.a.nome} class="max-h-64 max-w-xs object-contain bg-page" />
      </button>
    </Show>
  )
}

export function Mensagem(props: {
  m: ChatMensagem
  agrupada: boolean
  meuId: number
  eu?: { login: string; nome: string }
  ehAdmin: boolean
  naThread?: boolean
  aoResponder?: () => void
  aoReagir: (emoji: string) => void
  aoEditar: (texto: string) => void
  aoApagar: () => void
}) {
  const [editando, setEditando] = createSignal(false)
  const minha = () => props.m.autorId === props.meuId
  const acoes = (): JSX.Element => (
    <div class="absolute -top-3 right-3 hidden group-hover:flex group-focus-within:flex items-center gap-0.5 rounded-md border border-edge bg-panel px-1 py-0.5 shadow-sm">
      <For each={EMOJIS.slice(0, 4)}>
        {(e) => (
          <button type="button" class="rounded px-1 hover:bg-hover" title={`Reagir ${e}`} onClick={() => props.aoReagir(e)}>
            {e}
          </button>
        )}
      </For>
      <Show when={!props.naThread && props.aoResponder}>
        <button type="button" class="rounded px-1.5 text-xs text-ink-muted hover:bg-hover hover:text-ink" title="Responder em thread" onClick={() => props.aoResponder!()}>
          Responder
        </button>
      </Show>
      <Dropdown>
        <Dropdown.Trigger as="button" class="rounded px-1.5 text-ink-muted hover:bg-hover hover:text-ink leading-none" aria-label="Mais ações">
          ⋯
        </Dropdown.Trigger>
        <Dropdown.Content>
          <For each={EMOJIS}>{(e) => <Dropdown.Item onSelect={() => props.aoReagir(e)}>{e} Reagir</Dropdown.Item>}</For>
          <Show when={minha()}>
            <Dropdown.Item onSelect={() => setEditando(true)}>Editar</Dropdown.Item>
          </Show>
          <Show when={minha() || props.ehAdmin}>
            <Dropdown.Item onSelect={props.aoApagar}>
              <span class="text-failure">Apagar</span>
            </Dropdown.Item>
          </Show>
        </Dropdown.Content>
      </Dropdown>
    </div>
  )
  return (
    <div class="group relative flex gap-3 px-4 hover:bg-hover/60" classList={{ 'pt-3 pb-1': !props.agrupada, 'py-0.5': props.agrupada }} data-mensagem={props.m.id}>
      <div class="w-8 shrink-0">
        <Show when={!props.agrupada} fallback={<span class="hidden group-hover:block pt-0.5 text-[10px] text-ink-placeholder text-right tabular-nums">{hora(props.m.criadoEm)}</span>}>
          <Avatar id={props.m.autorId} nome={props.m.autor} />
        </Show>
      </div>
      <div class="min-w-0 flex-1">
        <Show when={!props.agrupada}>
          <div class="flex items-baseline gap-2">
            <span class="text-sm font-semibold text-ink">{props.m.autor}</span>
            <span class="text-[11px] text-ink-subtle tabular-nums">{hora(props.m.criadoEm)}</span>
          </div>
        </Show>
        <Show
          when={!props.m.excluido}
          fallback={<p class="text-sm italic text-ink-placeholder">(mensagem apagada)</p>}
        >
          <Show
            when={!editando()}
            fallback={
              <Editar
                inicial={props.m.texto}
                aoSalvar={(t) => (setEditando(false), props.aoEditar(t))}
                aoCancelar={() => setEditando(false)}
              />
            }
          >
            <Show when={props.m.texto}>
              <p class="text-sm text-ink leading-relaxed">
                <TextoRico texto={props.m.texto} eu={props.eu} />
                <Show when={props.m.editado}>
                  <span class="ml-1 text-[10px] text-ink-placeholder">(editada)</span>
                </Show>
              </p>
            </Show>
          </Show>
          <Show when={props.m.anexos.length > 0}>
            <div class="mt-1.5 flex flex-wrap gap-2">
              <For each={props.m.anexos}>{(a) => <Anexo a={a} />}</For>
            </div>
          </Show>
          <Show when={props.m.reacoes.length > 0}>
            <div class="mt-1 flex flex-wrap gap-1">
              <For each={props.m.reacoes}>
                {(r) => (
                  <button
                    type="button"
                    title={r.quem}
                    class="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs"
                    classList={{ 'border-accent/50 bg-accent/10 text-ink': r.minha, 'border-edge text-ink-muted hover:bg-hover': !r.minha }}
                    onClick={() => props.aoReagir(r.emoji)}
                  >
                    <span>{r.emoji}</span>
                    <span class="tabular-nums">{r.quantidade}</span>
                  </button>
                )}
              </For>
            </div>
          </Show>
          <Show when={!props.naThread && props.m.respostas > 0}>
            <button type="button" class="mt-1 text-xs font-medium text-accent hover:underline" onClick={() => props.aoResponder?.()}>
              {props.m.respostas} {props.m.respostas === 1 ? 'resposta' : 'respostas'}
            </button>
          </Show>
        </Show>
      </div>
      <Show when={!props.m.excluido && !editando()}>{acoes()}</Show>
    </div>
  )
}

function Editar(props: { inicial: string; aoSalvar: (t: string) => void; aoCancelar: () => void }) {
  let campo: HTMLTextAreaElement | undefined
  return (
    <div class="flex flex-col gap-1">
      <textarea
        ref={(el) => ((campo = el), requestAnimationFrame(() => (el.focus(), el.setSelectionRange(el.value.length, el.value.length))))}
        class="min-h-16 w-full rounded-md border border-accent bg-input px-2 py-1.5 text-sm text-ink"
        value={props.inicial}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) (e.preventDefault(), props.aoSalvar(campo!.value))
          else if (e.key === 'Escape') props.aoCancelar()
        }}
      />
      <span class="text-[11px] text-ink-subtle">Enter salva · Esc cancela</span>
    </div>
  )
}
